//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { BodyText } from '@components/atoms/text/Text';
import { SmallTooltipForIcon } from '@components/molecules/tooltip/Tooltip';
import { DarkGradientOverlay } from '@galleries/components/content/dark-gradient-overlay/DarkGradientOverlay';
import { InfoOverlay } from '@galleries/components/content/info-overlay/InfoOverlay';
import { MediaGrid } from '@galleries/components/content/media-grid/MediaGrid';
import SelectableImage from '@galleries/components/content/selected-image/SelectableImage';
import {
  BULK_RIGHTS_WORKFLOW_LARGE_MEDIA_WIDTH,
  BULK_RIGHTS_WORKFLOW_SMALL_MEDIA_WIDTH,
} from '@galleries/constants/galleriesConstants';
import { getContentItemImageUrl } from '@galleries/utils/getContentItemImageUrl';
import ItemId from '@models/ItemId';
import { isDefined } from '@utils/filterPredicates';

interface Props {
  selectedIndex: number;
  contentItems: IContentLibraryItem[];
  overlaidItems: Record<ItemId, true>;
  onIndexChange: (index: number) => void;
  imgOverlay: ReactNode;
}

const getNextIndex = (
  currentIndex: number,
  addend: number,
  maxIndex: number,
) => {
  const newIndex = currentIndex + addend;

  if (newIndex < 0) {
    return maxIndex;
  } else if (newIndex > maxIndex) {
    return 0;
  } else {
    return newIndex;
  }
};

const makeNavButton = (
  iconName: UnisonIconType,
  onClick: () => void,
  tooltip: string,
) => {
  return (
    <SmallTooltipForIcon
      target={
        <InvisibleButton onClick={onClick}>
          <NavIcon name={iconName} />
        </InvisibleButton>
      }
      content={<BodyText>{tooltip}</BodyText>}
    />
  );
};

const makeInfoOverlay = (contentItem: IContentLibraryItem) => (
  <>
    <DarkGradientOverlay />
    <InfoOverlay contentItem={contentItem} />
  </>
);

export const MediaCarousel = (props: Props) => {
  const {
    selectedIndex,
    onIndexChange,
    imgOverlay,
    contentItems,
    overlaidItems,
  } = props;

  const maxIndex = contentItems.length - 1;
  const heroContentItem = contentItems[selectedIndex];

  return (
    <>
      <HeroImgContainer>
        {maxIndex !== 0 &&
          makeNavButton(
            'chevron-left',
            () => onIndexChange(getNextIndex(selectedIndex, -1, maxIndex)),
            'Previous Image',
          )}
        <HeroImg>
          {isDefined(heroContentItem) && (
            <SelectableImage
              imgSrc={getContentItemImageUrl(
                heroContentItem,
                BULK_RIGHTS_WORKFLOW_LARGE_MEDIA_WIDTH,
              )}
              handleClick={() => undefined}
              hoverOverlay={makeInfoOverlay(heroContentItem)}
            />
          )}
        </HeroImg>
        {maxIndex !== 0 &&
          makeNavButton(
            'chevron-right',
            () => onIndexChange(getNextIndex(selectedIndex, 1, maxIndex)),
            'Next Image',
          )}
      </HeroImgContainer>
      <MediaGrid forceWidth={4} className='cu-mt-4'>
        {contentItems.map((contentItem, index) => (
          <PointerSelectableImage
            handleClick={() => onIndexChange(index)}
            imgSrc={getContentItemImageUrl(
              contentItem,
              BULK_RIGHTS_WORKFLOW_SMALL_MEDIA_WIDTH,
            )}
            showBorder={index === selectedIndex}
            isSelected={index === selectedIndex}
            key={index}
            overlay={overlaidItems[contentItem.id] && imgOverlay}
            hoverOverlay={
              !overlaidItems[contentItem.id] && makeInfoOverlay(contentItem)
            }
          />
        ))}
      </MediaGrid>
    </>
  );
};

const HeroImgContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavIcon = styled(Icon)`
  && {
    width: 30px;
    height: 30px;
  }
`;

const HeroImg = styled.div`
  width: 400px;
  height: 400px;
  margin: 0 ${({ theme }) => theme.spacings[6]};
  display: flex;
  justify-content: center;
`;

const PointerSelectableImage = styled(SelectableImage)`
  cursor: pointer;
`;
